import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User,
  Mail,
  Shield,
  CreditCard,
  FolderKanban,
  ExternalLink,
  LogOut,
  Calendar,
  Zap,
  Crown,
  Clock,
  Settings,
  KeyRound,
  Activity,
  Github,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Avatar } from '@/components/ui/Avatar';
import { Spinner } from '@/components/ui/Spinner';
import { useAuth } from '@/hooks/useAuth';
import { useProjects } from '@/hooks/useProject';
import { billingApi } from '@/api/billing';
import type { CreditBalance } from '@/types';
import { cn } from '@/utils/cn';

const ACCOUNTS_URL = 'https://accounts.applantus.com';

export function ProfilePage() {
  const navigate = useNavigate();
  const { user, logout, hasAnyRole } = useAuth();
  const { data: projectsData, isLoading: projectsLoading } = useProjects();
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [balanceLoading, setBalanceLoading] = useState(true);

  useEffect(() => {
    async function loadBalance() {
      try {
        const data = await billingApi.getBalance();
        setBalance(data);
      } catch (error) {
        console.error('Failed to load balance:', error);
      } finally {
        setBalanceLoading(false);
      }
    }
    loadBalance();
  }, []);

  const projectCount = projectsData?.totalCount || 0;
  const runningProjects = projectsData?.items?.filter(p => p.status === 'Running').length || 0;

  const getRoleBadgeVariant = (role: string) => {
    switch (role?.toLowerCase()) {
      case 'superadmin':
        return 'primary';
      case 'admin':
        return 'warning';
      case 'support':
        return 'info';
      default:
        return 'default';
    }
  };

  const getRoleDisplay = () => {
    if (!user?.roles || user.roles.length === 0) return 'User';
    const priority = ['superadmin', 'admin', 'support', 'user'];
    for (const role of priority) {
      if (user.roles.includes(role)) {
        return role.charAt(0).toUpperCase() + role.slice(1);
      }
    }
    return 'User';
  };

  const getTierIcon = () => {
    switch (user?.tier) {
      case 'Business':
        return <Crown className="w-5 h-5 text-yellow-500" />;
      case 'Pro':
      case 'Starter':
        return <Zap className="w-5 h-5 text-violet-500" />;
      case 'Hobby':
        return <Zap className="w-5 h-5 text-blue-500" />;
      default:
        return <User className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Profile</h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Manage your account settings and preferences
        </p>
      </div>

      {/* Profile Header Card */}
      <Card className="mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-primary-500 to-primary-700 h-24" />
        <div className="px-6 pb-6">
          {/* Avatar - overlaps gradient */}
          <div className="-mt-10 mb-4">
            <div className="ring-4 ring-white dark:ring-dark-800 rounded-full bg-white dark:bg-dark-800 inline-block">
              <Avatar
                src={user?.avatarUrl}
                name={user?.displayName || user?.email}
                size="xl"
              />
            </div>
          </div>
          {/* User info and actions - below gradient */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 flex-wrap">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {user?.displayName || 'User'}
                </h2>
                <Badge variant={getRoleBadgeVariant(getRoleDisplay())}>
                  {getRoleDisplay()}
                </Badge>
              </div>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{user?.email}</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                leftIcon={<ExternalLink className="w-4 h-4" />}
                onClick={() => window.open(ACCOUNTS_URL, '_blank')}
              >
                Edit Profile
              </Button>
              <Button
                variant="danger"
                leftIcon={<LogOut className="w-4 h-4" />}
                onClick={logout}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          icon={<FolderKanban className="w-5 h-5" />}
          label="Total Projects"
          value={projectsLoading ? '...' : projectCount.toString()}
          subtext={`${runningProjects} running`}
          color="blue"
        />
        <StatCard
          icon={<Zap className="w-5 h-5" />}
          label="Credits Available"
          value={balanceLoading ? '...' : ((balance?.balance ?? 0).toLocaleString())}
          subtext={balance?.monthlyCredits ? `${balance.monthlyCredits.toLocaleString()}/mo` : ''}
          color="violet"
        />
        <StatCard
          icon={getTierIcon()}
          label="Subscription"
          value={user?.tier || 'Free'}
          subtext="Current plan"
          color="amber"
          onClick={() => navigate('/billing')}
        />
        <StatCard
          icon={<Activity className="w-5 h-5" />}
          label="Account Status"
          value="Active"
          subtext="All systems operational"
          color="green"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-gray-500" />
              Account Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <InfoRow
                icon={<Mail className="w-4 h-4" />}
                label="Email"
                value={user?.email || 'N/A'}
              />
              <InfoRow
                icon={<User className="w-4 h-4" />}
                label="Display Name"
                value={user?.displayName || 'N/A'}
              />
              {user?.given_name && (
                <InfoRow
                  icon={<User className="w-4 h-4" />}
                  label="First Name"
                  value={user.given_name}
                />
              )}
              {user?.family_name && (
                <InfoRow
                  icon={<User className="w-4 h-4" />}
                  label="Last Name"
                  value={user.family_name}
                />
              )}
              <InfoRow
                icon={<Shield className="w-4 h-4" />}
                label="Role"
                value={getRoleDisplay()}
              />
              <div className="pt-4 border-t border-gray-200 dark:border-dark-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  To update your personal information, company details, or contact info:
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ExternalLink className="w-4 h-4" />}
                  onClick={() => window.open(ACCOUNTS_URL, '_blank')}
                >
                  Manage at Applantus Accounts
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Security & Access */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-gray-500" />
              Security & Access
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <InfoRow
                icon={<Shield className="w-4 h-4" />}
                label="Authentication"
                value="Applantus SSO"
              />
              <InfoRow
                icon={<KeyRound className="w-4 h-4" />}
                label="Identity Provider"
                value="Keycloak"
              />
              <InfoRow
                icon={<Clock className="w-4 h-4" />}
                label="Session"
                value="Active"
                badge={<Badge variant="success" size="sm">Secure</Badge>}
              />
              {user?.sub && (
                <InfoRow
                  icon={<User className="w-4 h-4" />}
                  label="User ID"
                  value={user.sub.substring(0, 8) + '...'}
                  className="font-mono text-sm"
                />
              )}
              <div className="pt-4 border-t border-gray-200 dark:border-dark-700">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  To change your password or manage security settings:
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ExternalLink className="w-4 h-4" />}
                  onClick={() => window.open(`${ACCOUNTS_URL}/security`, '_blank')}
                >
                  Security Settings
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Subscription & Billing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-gray-500" />
              Subscription & Billing
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getTierIcon()}
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {user?.tier || 'Free'} Plan
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {balance?.monthlyCredits ? `${balance.monthlyCredits.toLocaleString()} credits/month` : 'Basic features'}
                    </p>
                  </div>
                </div>
                <Badge variant={user?.tier === 'Free' ? 'default' : 'success'}>
                  {user?.tier === 'Free' ? 'Free' : 'Active'}
                </Badge>
              </div>

              {balance && balance.monthlyCredits > 0 && (
                <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Credits Used</span>
                    <span className="text-sm font-medium text-gray-900 dark:text-white">
                      {(balance.usedThisMonth ?? 0).toLocaleString()} / {(balance.monthlyCredits ?? 0).toLocaleString()}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                    <div
                      className="bg-violet-500 h-2 rounded-full transition-all"
                      style={{
                        width: `${Math.min(((balance.usedThisMonth ?? 0) / (balance.monthlyCredits || 1)) * 100, 100)}%`,
                      }}
                    />
                  </div>
                  {balance.nextResetDate && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Resets {formatDate(balance.nextResetDate)}
                    </p>
                  )}
                </div>
              )}

              <div className="pt-4 border-t border-gray-200 dark:border-dark-700 flex gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate('/billing')}
                >
                  Manage Subscription
                </Button>
                {user?.tier === 'Free' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/billing')}
                  >
                    Upgrade
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-gray-500" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <QuickAction
                icon={<FolderKanban className="w-5 h-5" />}
                title="View Projects"
                description="Manage your applications"
                onClick={() => navigate('/dashboard')}
              />
              <QuickAction
                icon={<Zap className="w-5 h-5" />}
                title="Buy Credits"
                description="Purchase additional credits"
                onClick={() => navigate('/billing')}
              />
              <QuickAction
                icon={<Github className="w-5 h-5" />}
                title="GitHub Integration"
                description="Connect and sync repositories"
                onClick={() => navigate('/settings/github')}
              />
              <QuickAction
                icon={<ExternalLink className="w-5 h-5" />}
                title="Edit Profile"
                description="Update at Applantus Accounts"
                onClick={() => window.open(ACCOUNTS_URL, '_blank')}
              />
              {hasAnyRole(['admin', 'superadmin']) && (
                <QuickAction
                  icon={<Shield className="w-5 h-5" />}
                  title="Admin Dashboard"
                  description="Platform administration"
                  onClick={() => navigate('/admin')}
                  highlight
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Danger Zone */}
      <Card className="mt-6 border-red-200 dark:border-red-900/50">
        <CardHeader>
          <CardTitle className="text-red-600 dark:text-red-400">Danger Zone</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Sign out of all devices</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                This will sign you out from all active sessions
              </p>
            </div>
            <Button
              variant="danger"
              size="sm"
              onClick={logout}
            >
              Sign Out Everywhere
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  subtext,
  color,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  subtext?: string;
  color: 'blue' | 'violet' | 'amber' | 'green';
  onClick?: () => void;
}) {
  const colorStyles = {
    blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    violet: 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400',
    amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400',
    green: 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400',
  };

  return (
    <Card
      hover={!!onClick}
      onClick={onClick}
      className={cn(onClick && 'cursor-pointer')}
    >
      <div className="flex items-center gap-3">
        <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', colorStyles[color])}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
          {subtext && (
            <p className="text-xs text-gray-500 dark:text-gray-400">{subtext}</p>
          )}
        </div>
      </div>
    </Card>
  );
}

function InfoRow({
  icon,
  label,
  value,
  badge,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  badge?: React.ReactNode;
  className?: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
        {icon}
        <span className="text-sm">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className={cn('text-sm font-medium text-gray-900 dark:text-white', className)}>
          {value}
        </span>
        {badge}
      </div>
    </div>
  );
}

function QuickAction({
  icon,
  title,
  description,
  onClick,
  highlight,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
  highlight?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors',
        highlight
          ? 'bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30'
          : 'hover:bg-gray-50 dark:hover:bg-dark-700'
      )}
    >
      <div className={cn(
        'w-10 h-10 rounded-lg flex items-center justify-center',
        highlight
          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
          : 'bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400'
      )}>
        {icon}
      </div>
      <div>
        <p className={cn(
          'font-medium',
          highlight
            ? 'text-primary-600 dark:text-primary-400'
            : 'text-gray-900 dark:text-white'
        )}>
          {title}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">{description}</p>
      </div>
    </button>
  );
}
